import React, { useEffect, useState } from "react";
import axios from "../api";
import {
  Typography,
  Container,
  Paper,
  Grid,
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Pagination,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  MarkEmailRead,
  HourglassEmpty,
  Inbox,
  AssignmentTurnedIn,
  Send,
  WarningAmber,
  GppMaybe,
  DeleteSweep,
  VerifiedUser,
  Api,
} from "@mui/icons-material";
import AdminLayout from "./AdminLayout";
import StatCard from "./common/StatCard";
import UndertakingDocuments from "./common/UndertakingDocuments";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PER_PAGE = 10;

const severityColor = {
  error: "error",
  warning: "warning",
  info: "info",
};

function AdminPanel({ onLogout }) {
  const theme = useTheme();
  const [records, setRecords] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [stats, setStats] = useState({});
  const [attention, setAttention] = useState([]);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectTargetId, setRejectTargetId] = useState(null);

  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem("adminUser"));
  const isEmployee = admin?.role === "Employee";

  useEffect(() => {
    fetchRecords();
    fetchInvites();
    fetchStats();
    if (!isEmployee) {
      axios
        .get("/assets/attention")
        .then((r) => setAttention(r.data.result?.items || []))
        .catch(() => setAttention([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecords = async () => {
    const resp = await axios.get("/undertaking/all");
    setRecords(resp.data);
  };

  const fetchInvites = async () => {
    const resp = await axios.get("/undertaking/invites");
    setInvites(resp.data);
  };

  const fetchStats = async () => {
    const resp = await axios.get("/undertaking/stats");
    setStats(resp.data);
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.warning("Please enter email");
      return;
    }

    try {
      await axios.post("/undertaking/send-invite", { email: inviteEmail });
      toast.success("Undertaking form invite sent.");
      setInviteEmail("");
      fetchInvites();
      fetchStats();
    } catch (err) {
      console.error("Error sending invite", err);
      toast.error(err.response?.data?.error || "Error sending invite.");
    }
  };

  const handleLogout = () => {
    if (onLogout) return onLogout();
    navigate("/login");
  };

  const handlePreview = (email) => {
    const record = records.find((rec) => rec.email === email);
    if (record) {
      setSelectedRecord(record);
      setPreviewDialog(true);
    } else {
      toast.warning("No submission found for this email");
    }
  };

  const handleClosePreview = () => {
    setPreviewDialog(false);
    setSelectedRecord(null);
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`/undertaking/approve/${id}`);
      toast.success("Submission approved and asset recorded.");
      handleClosePreview();
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.error || "Approve failed");
    }
  };

  const openRejectDialog = (id) => {
    setRejectTargetId(id);
    setRejectReason("");
    setRejectDialog(true);
  };

  const handleReject = async () => {
    if (!rejectTargetId) return;
    try {
      await axios.post(`/undertaking/reject/${rejectTargetId}`, {
        reason: rejectReason,
      });
      toast.success("Submission rejected.");
      setRejectDialog(false);
      setRejectTargetId(null);
      setRejectReason("");
      handleClosePreview();
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.error || "Reject failed");
    }
  };

  const pendingInvites = invites.filter((inv) => inv.status === "Pending");
  const acceptedInvites = invites.filter((inv) => inv.status === "Accepted");
  const tabRows =
    tab === 0 ? pendingInvites : tab === 1 ? acceptedInvites : invites;
  const pageCount = Math.ceil(tabRows.length / PER_PAGE) || 1;
  const safePage = Math.min(page, pageCount);
  const pagedRows = tabRows.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const inviteStatusChip = (status) => (
    <Chip
      size="small"
      label={status}
      color={status === "Accepted" ? "success" : status === "Pending" ? "warning" : "default"}
      variant={status === "Pending" ? "outlined" : "filled"}
    />
  );

  const PreviewDialog = () => (
    <Dialog
      open={previewDialog}
      onClose={handleClosePreview}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Asset Undertaking Details - {selectedRecord?.email}
      </DialogTitle>
      <DialogContent>
        {selectedRecord && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Employee Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Employee Name
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.EmpName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Employee ID
                </Typography>
                <Typography variant="body1">{selectedRecord.EmpID}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Manager
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.Manager}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Department
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.Department}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Title
                </Typography>
                <Typography variant="body1">{selectedRecord.Title}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Phone
                </Typography>
                <Typography variant="body1">{selectedRecord.phone}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Address Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  City
                </Typography>
                <Typography variant="body1">{selectedRecord.City}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  State
                </Typography>
                <Typography variant="body1">{selectedRecord.State}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Country
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.Country}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  ZIP Code
                </Typography>
                <Typography variant="body1">{selectedRecord.Zip}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Asset Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Asset ID
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.assetId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Serial Number
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.serialNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Model
                </Typography>
                <Typography variant="body1">{selectedRecord.Model}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Model Number
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.ModelNo}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Manufacturer
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.Manufacturer}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Received By
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.Receivedby}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Specifications
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.Specifications}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Accessories
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.accessories}
                </Typography>
              </Grid>
              {selectedRecord.AccessoriesExtra && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Additional Accessories
                  </Typography>
                  <Typography variant="body1">
                    {selectedRecord.AccessoriesExtra}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Submission Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Submission Date
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedRecord.timestamp).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  IP Address
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.ipAddress}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Terms Accepted
                </Typography>
                <Chip
                  label={selectedRecord.termsAccepted ? "Yes" : "No"}
                  color={selectedRecord.termsAccepted ? "success" : "error"}
                  size="small"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <UndertakingDocuments
              idProofPath={selectedRecord.idProofPath}
              signaturePath={selectedRecord.signaturePath}
              assetImages={selectedRecord.assetImages}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {selectedRecord && (
          <Chip
            size="small"
            label={selectedRecord.reviewStatus || "Submitted"}
            color={
              selectedRecord.reviewStatus === "Approved"
                ? "success"
                : selectedRecord.reviewStatus === "Rejected"
                ? "error"
                : "warning"
            }
            sx={{ mr: "auto" }}
          />
        )}
        {selectedRecord && selectedRecord.reviewStatus !== "Approved" && (
          <Button color="success" onClick={() => handleApprove(selectedRecord._id)}>
            Approve
          </Button>
        )}
        {selectedRecord && selectedRecord.reviewStatus !== "Rejected" && (
          <Button color="error" onClick={() => openRejectDialog(selectedRecord._id)}>
            Reject
          </Button>
        )}
        <Button onClick={handleClosePreview} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <AdminLayout
      title="Dashboard"
      subtitle="Invite employees to complete undertakings — approve submissions here to create/link inventory. Device corrections from My Devices go to Corrections."
      onLogout={handleLogout}
    >
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          {!isEmployee && attention.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.warning.main,
                  0.06
                )}, transparent 70%)`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                <WarningAmber color="warning" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Needs attention
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click a chip to jump to the related screen
                </Typography>
              </Stack>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {attention.map((item) => (
                  <Chip
                    key={item.key}
                    clickable
                    color={severityColor[item.severity] || "default"}
                    variant={item.severity === "info" ? "outlined" : "filled"}
                    label={`${item.label}: ${item.count}`}
                    onClick={() => navigate(item.href || "/dashboard")}
                    icon={
                      item.key.startsWith("warranty") ? (
                        <GppMaybe />
                      ) : item.key.startsWith("verification") ? (
                        <VerifiedUser />
                      ) : item.key.startsWith("recycle") ? (
                        <DeleteSweep />
                      ) : item.key === "failedWebhooks" ? (
                        <Api />
                      ) : (
                        <WarningAmber />
                      )
                    }
                  />
                ))}
              </Stack>
            </Paper>
          )}

          <Box
            sx={{
              display: "grid",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              label="Total Invites"
              value={isEmployee ? "-" : stats.totalInvites || 0}
              icon={<Send />}
              color="primary"
            />
            <StatCard
              label="Pending Invites"
              value={isEmployee ? "-" : stats.totalPending || 0}
              icon={<HourglassEmpty />}
              color="warning"
            />
            <StatCard
              label="Submitted"
              value={isEmployee ? "-" : stats.totalAccepted || 0}
              icon={<MarkEmailRead />}
              color="info"
            />
            <StatCard
              label="Total Submissions"
              value={isEmployee ? "-" : stats.totalSubmissions || 0}
              icon={<AssignmentTurnedIn />}
              color="success"
              helper="Undertaking forms — not Verification status"
            />
          </Box>

          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Send undertaking form invite
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Invite an employee to complete the <strong>asset undertaking form</strong> (public
              form link). This is not account activation.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              To create a login account, use Users → Add &amp; Invite.
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                label="User Email"
                variant="outlined"
                fullWidth
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Inbox fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleInvite}
                disabled={isEmployee}
                startIcon={<Send />}
                sx={{ px: 4, whiteSpace: "nowrap" }}
              >
                Send
              </Button>
            </Box>
          </Paper>

          <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
            <Tabs
              value={tab}
              onChange={(e, newTab) => {
                setTab(newTab);
                setPage(1);
              }}
            >
              <Tab label="Pending invites" />
              <Tab label="Submitted (approve into inventory)" />
              <Tab label="All invites" />
            </Tabs>

            {tab === 0 && !isEmployee && (
              <>
              <Table sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invited Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((invite) => (
                    <TableRow key={invite._id}>
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>{inviteStatusChip(invite.status)}</TableCell>
                      <TableCell>
                        {new Date(invite.sentAt).toLocaleString().split("T")[0]}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pendingInvites.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No pending invites
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {pendingInvites.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={pageCount}
                    page={safePage}
                    onChange={(e, v) => setPage(v)}
                  />
                </Box>
              )}
              </>
            )}
            {tab === 1 && !isEmployee && (
              <>
              <Table sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invited Date</TableCell>
                    <TableCell>Preview</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((invite) => (
                    <TableRow key={invite._id}>
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>{inviteStatusChip(invite.status)}</TableCell>
                      <TableCell>
                        {new Date(invite.sentAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePreview(invite.email)}
                        >
                          Preview
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {acceptedInvites.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No submitted requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {acceptedInvites.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={pageCount}
                    page={safePage}
                    onChange={(e, v) => setPage(v)}
                  />
                </Box>
              )}
              </>
            )}

            {tab === 2 && !isEmployee && (
              <>
              <Table sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invited Date</TableCell>
                    <TableCell>Submitted Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((invite) => {
                    const matchedRecord = records.find(
                      (rec) => rec.email === invite.email
                    );
                    const submittedDate = matchedRecord?.timestamp
                      ? new Date(matchedRecord.timestamp).toLocaleString()
                      : "Not Submitted";

                    return (
                      <TableRow key={invite._id}>
                        <TableCell>{invite.email}</TableCell>
                        <TableCell>{invite.status}</TableCell>
                        <TableCell>
                          {invite.sentAt
                            ? new Date(invite.sentAt).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell>{submittedDate}</TableCell>
                      </TableRow>
                    );
                  })}
                  {invites.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No invites sent
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {invites.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={pageCount}
                    page={safePage}
                    onChange={(e, v) => setPage(v)}
                  />
                </Box>
              )}
              </>
            )}
          </Paper>
        </Container>

      <PreviewDialog />

      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject submission</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason for rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-center" autoClose={2500} />
    </AdminLayout>
  );
}

export default AdminPanel;
